import { useEffect, useRef, useState } from 'react';
import { loadDetectors, loadRecognition, detectLive, getDescriptor, webglInfo, faceIsSlow, warmup } from '../utils/faceapi';
import { faceStatus, registerFace, faceCheckin } from '../api/attendance';
import { getPublicIp } from '../utils/publicIp';

// Chấm công online bằng khuôn mặt (mọi xử lý AI chạy trong trình duyệt NV).
// - Chưa đăng ký -> luồng ĐĂNG KÝ (chụp 5 mẫu + đồng ý sinh trắc).
// - Đã đăng ký -> luồng CHẤM CÔNG (thử thách nháy mắt -> chụp -> gửi vector khớp ở server).
export default function FaceAttendanceModal({ onClose, toast }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const ipRef = useRef(''); // IP public do trình duyệt tự lấy (gửi kèm khi server sau proxy chỉ thấy 127.0.0.1)
  const [ready, setReady] = useState(false); // model + camera sẵn sàng
  const [st, setSt] = useState(null); // faceStatus từ server
  const [mode, setMode] = useState('checkin'); // 'checkin' | 'register'
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [consent, setConsent] = useState(false);
  const [shots, setShots] = useState([]); // vector khi đăng ký
  const [fatal, setFatal] = useState(''); // lỗi chặn không dùng được (vd máy không có WebGL)

  useEffect(() => {
    let alive = true;
    // Chặn sớm 2 trường hợp gây TREO ("Trang không phản hồi") vì AI phải chạy trên CPU đồng bộ:
    //  (1) máy không có WebGL;  (2) WebGL chạy PHẦN MỀM (GPU bị tắt/chặn — hay gặp ở laptop Chrome).
    const gl = webglInfo();
    if (!gl.ok) {
      setFatal('Máy hoặc trình duyệt này không hỗ trợ tăng tốc đồ hoạ (WebGL) nên không chấm công bằng khuôn mặt được. Hãy dùng Chrome/Edge mới nhất hoặc chấm công trên máy khác.');
      return;
    }
    if (gl.software) {
      setFatal('Chrome trên máy này đang TẮT tăng tốc phần cứng (GPU) — nhận diện khuôn mặt sẽ treo. Cách bật: mở Chrome → dấu ⋮ → Cài đặt → Hệ thống → BẬT “Sử dụng tính năng tăng tốc phần cứng khi khả dụng” → Khởi động lại Chrome. (Kiểm tra tại chrome://gpu — mục WebGL phải là “Hardware accelerated”.) Nếu không bật được, dùng máy khác.');
      return;
    }
    // Cảnh báo nếu tải lâu (máy yếu) — để NV bấm "Chờ" thay vì thoát.
    const slowT = setTimeout(() => { if (alive) setMsg('Đang tải mô hình nhận diện… máy yếu có thể mất 20–40 giây, vui lòng bấm “Chờ” nếu trình duyệt hỏi.'); }, 6000);
    (async () => {
      try {
        setMsg('Đang mở camera…');
        // THỨ TỰ QUAN TRỌNG: mở CAMERA trước, nạp model SAU.
        // Nạp model = import TF.js + khởi tạo WebGL + biên dịch shader -> CHẶN main thread vài giây.
        // Gọi trước getUserMedia thì khung hình chưa kịp vẽ -> đứng ở "Đang mở camera…" và Chrome
        // báo "Trang không phản hồi" (đúng lỗi nhân viên gặp).
        const statusP = (async () => {
          const myIp = await getPublicIp().catch(() => '');
          ipRef.current = myIp;
          return faceStatus(myIp).catch(() => null);
        })();
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 480 }, audio: false });
        if (!alive) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
        const s = await statusP;
        if (!alive) return;
        setSt(s);
        setMode(s && !s.registered ? 'register' : 'checkin');
        setMsg('Đang tải mô hình nhận diện…');
        // Nhường trình duyệt VẼ XONG khung hình camera rồi mới nạp model (đoạn chặn main thread).
        await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 0)));
        await loadDetectors();
        if (!alive) return;
        // LÀM NÓNG ngay bây giờ (còn đang màn "Đang tải…"): lượt detect đầu phải biên dịch shader
        // — máy GPU phần mềm chặn main thread vài giây; để nó rơi vào lúc làm thử thách là treo
        // "Trang không phản hồi" giữa chừng. Nóng máy xong thì các lượt sau nhẹ hơn nhiều.
        setMsg('Đang khởi động nhận diện…');
        await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 0)));
        await warmup(videoRef.current);
        if (!alive) return;
        setReady(true);
        // Máy bị TẮT tăng tốc GPU (WebGL phần mềm) -> nhận diện chậm; báo trước + hướng xử lý.
        setMsg(faceIsSlow()
          ? '⚠ Máy này đang TẮT tăng tốc đồ hoạ nên nhận diện sẽ CHẬM. Bật lại: Cài đặt Chrome → Hệ thống → "Tăng tốc đồ họa" → bật, rồi mở lại Chrome.'
          : '');
        // Recognition net (6MB) nạp lúc RẢNH, không chặn thao tác. getDescriptor() tự chờ nếu NV
        // bấm chấm công trước khi tải xong -> không sợ thiếu.
        const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1500));
        idle(() => { if (alive) loadRecognition().catch(() => {}); });
      } catch (e) {
        // Nói RÕ nguyên nhân thay vì nuốt lỗi -> NV tự xử lý được, và dễ hỗ trợ từ xa.
        const n = e?.name || '';
        const why =
          n === 'NotAllowedError' ? 'Bạn đã CHẶN quyền camera. Bấm biểu tượng 🔒/📷 trên thanh địa chỉ → cho phép Camera → tải lại trang.'
          : n === 'NotFoundError' || n === 'DevicesNotFoundError' ? 'Máy này không tìm thấy camera nào. Hãy cắm/bật webcam rồi thử lại.'
          : n === 'NotReadableError' ? 'Camera đang bị ứng dụng khác chiếm (Zoom/Teams/Zalo…). Hãy tắt app đó rồi thử lại.'
          : !window.isSecureContext ? 'Trang phải chạy HTTPS mới dùng được camera.'
          : `Không mở được camera hoặc mô hình nhận diện. (${n || e?.message || 'lỗi không rõ'})`;
        setMsg(why);
      } finally {
        clearTimeout(slowT);
      }
    })();
    return () => { alive = false; clearTimeout(slowT); streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, []);

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // Ảnh xem trước (JPEG thu nhỏ) để Giám đốc đối chiếu khi duyệt.
  function snapshot() {
    const v = videoRef.current;
    if (!v?.videoWidth) return '';
    const w = 320, h = Math.round((v.videoHeight / v.videoWidth) * 320);
    const c = document.createElement('canvas'); c.width = w; c.height = h;
    c.getContext('2d').drawImage(v, 0, 0, w, h);
    return c.toDataURL('image/jpeg', 0.7);
  }

  // Chụp 1 mẫu khuôn mặt (đăng ký).
  async function captureShot() {
    if (busy || !ready) return;
    setBusy(true);
    try {
      const d = await getDescriptor(videoRef.current);
      if (!d) { toast('Không thấy khuôn mặt — đưa mặt vào giữa khung.', 'danger'); return; }
      setShots((s) => [...s, d]);
    } finally { setBusy(false); }
  }

  async function submitRegister() {
    if (shots.length < 3) { toast('Cần ít nhất 3 mẫu (bấm "Chụp mẫu").', 'danger'); return; }
    setBusy(true);
    try {
      await registerFace(shots, consent, snapshot());
      toast('Đã gửi đăng ký khuôn mặt — chờ Giám đốc duyệt.');
      setSt((s) => ({ ...s, registered: true, approved: false, consented: true }));
      setShots([]); setMode('checkin');
    } catch (e) { toast(e.response?.data?.message || 'Đăng ký thất bại', 'danger'); }
    finally { setBusy(false); }
  }

  // Sinh chuỗi 3 động tác NGẪU NHIÊN (nháy mắt / quay đầu) — phải làm đúng trình tự → chống video quay sẵn.
  function makeChallenge() {
    const A = ['blink', 'turn'];
    const s = Array.from({ length: 3 }, () => A[Math.random() < 0.5 ? 0 : 1]);
    if (s.every((x) => x === s[0])) s[1] = s[0] === 'blink' ? 'turn' : 'blink'; // đảm bảo có cả 2 loại
    return s;
  }
  const LABEL = { blink: '👁 NHÁY MẮT', turn: '↔️ QUAY ĐẦU sang trái hoặc phải' };

  // Thử thách theo lệnh: mỗi bước reset mốc, phát hiện đúng động tác rồi mới sang bước sau (mỗi bước ≤8s).
  async function runLivenessAndCapture() {
    const need = st?.config?.livenessRequired !== false;
    if (!need) { const d = await getDescriptor(videoRef.current); return d ? { descriptor: d, liveness: false } : null; }
    const steps = makeChallenge();
    for (let i = 0; i < steps.length; i++) {
      const act = steps[i];
      let earMx = 0, earMn = 1, yawMx = -1, yawMn = 1, faceSeen = false, done = false;
      const t0 = Date.now();
      // Máy chậm: mỗi lượt detect lâu hơn nhiều -> nới thời gian mỗi bước 8s -> 15s cho đủ lượt.
      const stepMs = faceIsSlow() ? 15000 : 8000;
      while (!done && Date.now() - t0 < stepMs) {
        const s = await detectLive(videoRef.current);
        if (s) {
          faceSeen = true;
          earMx = Math.max(earMx, s.ear); earMn = Math.min(earMn, s.ear);
          yawMx = Math.max(yawMx, s.yaw); yawMn = Math.min(yawMn, s.yaw);
          done = act === 'blink' ? (earMx >= 0.18 && earMx - earMn >= 0.07) : (yawMx - yawMn >= 0.14);
          setMsg(`Bước ${i + 1}/${steps.length}: ${LABEL[act]}`);
        } else setMsg('Đưa mặt vào giữa khung, đủ sáng…');
        // Máy chậm (GPU phần mềm): giãn nhịp 300ms để main thread còn thở giữa các lượt detect
        // (dí 80ms liên tục là Chrome báo "Trang không phản hồi" ngay giữa thử thách).
        await sleep(faceIsSlow() ? 300 : 80);
      }
      if (!done) return faceSeen ? { failLive: true } : null; // hết giờ 1 bước -> chưa đạt
    }
    setMsg('✅ Đạt! Giữ yên…');
    const d = await getDescriptor(videoRef.current);
    return d ? { descriptor: d, liveness: true } : null;
  }

  async function doCheckin(type) {
    if (busy || !ready) return;
    setBusy(true);
    try {
      const r = await runLivenessAndCapture();
      setMsg('');
      if (!r) { toast('Không thấy khuôn mặt — đưa mặt vào khung, đủ sáng.', 'danger'); return; }
      if (r.failLive) { toast('Chưa làm đúng chuỗi động tác trong thời gian cho phép. Thử lại.', 'danger'); return; }
      const res = await faceCheckin(r.descriptor, type, r.liveness, ipRef.current);
      toast(`✅ Đã chấm ${type === 'out' ? 'RA' : 'VÀO'} lúc ${res.time}. (khớp ${Number(res.distance).toFixed(2)})`);
      onClose();
    } catch (e) { toast(e.response?.data?.message || 'Chấm công thất bại', 'danger'); }
    finally { setBusy(false); setMsg(''); }
  }

  const ipWarn = st && st.config?.ipCheckEnabled && !st.ipOk;

  return (
    <div>
      <span className="close" onClick={onClose}>×</span>
      <h3 style={{ marginTop: 0 }}>📷 Chấm công bằng khuôn mặt</h3>

      {fatal ? (
        <div style={{ padding: '14px 16px', borderRadius: 10, background: 'color-mix(in srgb, var(--danger) 12%, transparent)', color: 'var(--danger)', fontSize: 14, lineHeight: 1.5 }}>
          ⚠️ {fatal}
        </div>
      ) : (
      <>
      <div style={{ position: 'relative', width: '100%', maxWidth: 360, margin: '0 auto', aspectRatio: '4/3', background: '#000', borderRadius: 12, overflow: 'hidden' }}>
        <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
        {msg && <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '8px 10px', background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 13, textAlign: 'center' }}>{msg}</div>}
      </div>

      {ipWarn && <div className="small" style={{ color: 'var(--danger)', marginTop: 8, textAlign: 'center' }}>Bạn đang không ở trong mạng Wifi công ty (IP {st.ip}).</div>}

      {mode === 'register' ? (
        <div style={{ marginTop: 12 }}>
          <div className="small muted" style={{ marginBottom: 8 }}>Đăng ký khuôn mặt: chụp 3–5 mẫu ở các góc/ánh sáng khác nhau. Sau khi gửi sẽ <b>chờ Giám đốc duyệt</b>. Dữ liệu chấm công lưu dạng vector; ảnh chỉ để GĐ đối chiếu rồi xoá.</div>
          <label className="row" style={{ gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
            <input type="checkbox" style={{ width: 'auto', marginTop: 3 }} checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span className="small">Tôi đồng ý cho công ty dùng dữ liệu khuôn mặt để chấm công.</span>
          </label>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button className="btn ghost" disabled={busy || !ready} onClick={captureShot}>📸 Chụp mẫu ({shots.length}/5)</button>
            {shots.length > 0 && <button className="btn ghost sm" disabled={busy} onClick={() => setShots([])}>Xóa</button>}
            <div className="spacer" />
            <button className="btn" disabled={busy || shots.length < 3 || !consent} onClick={submitRegister}>Lưu đăng ký</button>
          </div>
          {st?.registered && <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={() => setMode('checkin')}>← Quay lại chấm công</button>}
        </div>
      ) : st && st.registered && !st.approved ? (
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <div style={{ padding: '10px 14px', borderRadius: 10, background: 'color-mix(in srgb, var(--warn-fill) 16%, transparent)', color: 'var(--warn)', fontWeight: 600 }}>
            ⏳ Khuôn mặt đang chờ Giám đốc duyệt. Bạn sẽ nhận thông báo khi được duyệt.
          </div>
          {/* ĐÃ đăng ký -> KHÔNG cho tự đăng ký lại (tránh đăng ký đè/gian lận). Cần đổi thì báo Giám đốc. */}
          <div className="small muted" style={{ marginTop: 10 }}>Cần đăng ký lại? Vui lòng báo Giám đốc.</div>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          <div className="row" style={{ gap: 10, justifyContent: 'center' }}>
            <button className="btn" disabled={busy || !ready || ipWarn} onClick={() => doCheckin('in')}>🟢 Chấm VÀO</button>
            <button className="btn" disabled={busy || !ready || ipWarn} onClick={() => doCheckin('out')}>🔴 Chấm RA</button>
          </div>
          {/* ĐÃ đăng ký khuôn mặt -> KHÔNG hiện nút đăng ký lại nữa. Chỉ NV chưa đăng ký mới vào mode 'register'. */}
          {!st?.registered && (
            <button className="btn ghost sm" style={{ marginTop: 12 }} disabled={busy} onClick={() => { setShots([]); setMode('register'); }}>Đăng ký khuôn mặt</button>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
}
